import type { CharacterShape, Stroke } from "./stroke-shape.js";
import { cheapestPairing } from "./cheapest-assignment.js";
import { strokeCost } from "./stroke-distance.js";

/**
 * Which of a character's strokes a reader drew, and in what order.
 *
 * The grader says whether the ink was the character the card asked for. This
 * says what to show a reader about each stroke of that character, so a marked
 * card can colour the strokes they got right apart from the ones they missed
 * or put in the wrong place.
 *
 * **Each drawn stroke is taken for at most one of the character's, or for
 * none.** The pairing is the cheapest one, as `stroke-assignment.ts` makes it,
 * over a matrix widened so that any stroke on either side may go unpaired at a
 * fixed price. A drawn stroke too unlike every stroke of the character is
 * therefore left over rather than forced onto the nearest, and a stroke of the
 * character nothing resembles is left missing. Leaving one of each unpaired
 * costs {@link strokeUnpaired} twice, so a pair is taken only where it costs
 * less than that.
 *
 * **The order is read off the pairs that kept it.** The longest run of pairs
 * whose places in the character rise as the reader drew them is the order the
 * reader had right. Every other pair is a stroke written out of turn. A reader
 * who left one stroke out has every other stroke in order, which comparing
 * each stroke's place against its own number would have called all wrong
 * after the gap.
 *
 * `docs/fuxi.md` under "Which strokes were right" has the threshold measured.
 */

/** What became of one stroke of the character. */
export type StrokeMark = "right" | "order" | "missing";

/** What a reader's ink made of each stroke of one character. */
export interface StrokeComparison {
  /** One mark per stroke of the character, in writing order. */
  readonly marks: readonly StrokeMark[];

  /** How many drawn strokes were taken for none of the character's. */
  readonly extra: number;
}

/**
 * The price of leaving one stroke unpaired, on either side.
 *
 * Half the dearest pair the comparison will accept. See the module comment.
 */
export const strokeUnpaired = 0.35;

/**
 * What a pair costs for each place between the drawn stroke and the stored one.
 *
 * A tie-break. Two horizontals in one character cost about the same against
 * each other's places, and the cheapest pairing could cross them and report a
 * stroke out of order that the reader wrote in turn.
 */
export const strokeOrderLean = 0.02;

/**
 * The longest run of pairs whose stored places rise, as a set of pair indices.
 *
 * Patience sorting, which is n log n and keeps the indices to walk back.
 */
function risingRun(places: readonly number[]): ReadonlySet<number> {
  const tails: number[] = [];
  const before = Array.from({ length: places.length }, () => -1);

  places.forEach((place, at) => {
    let low = 0;
    let high = tails.length;

    while (low < high) {
      const middle = (low + high) >> 1;

      if ((places[tails[middle] as number] as number) < place) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }

    before[at] = low > 0 ? (tails[low - 1] as number) : -1;
    tails[low] = at;
  });

  const kept = new Set<number>();

  for (let at = tails.at(-1) ?? -1; at !== -1; at = before[at] as number) {
    kept.add(at);
  }

  return kept;
}

/**
 * Compare the strokes a reader drew with the character's own.
 *
 * @param written - The strokes the reader drew, in the order they drew them.
 * @param stored - The character's own strokes, in writing order.
 * @param unpaired - The price of leaving a stroke unpaired. The measured
 * {@link strokeUnpaired} unless a measurement is asking.
 * @returns A mark for each stroke of the character, and the strokes left over.
 */
export function compareStrokes(
  written: CharacterShape,
  stored: CharacterShape,
  unpaired = strokeUnpaired,
  lean = strokeOrderLean,
): StrokeComparison {
  const size = written.length + stored.length;
  const costs = Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, column) => {
      const drawn = row < written.length;
      const kept = column < stored.length;

      if (drawn && kept) {
        return (
          strokeCost(written[row] as Stroke, stored[column] as Stroke) +
          lean * Math.abs(row - column)
        );
      }

      return drawn || kept ? unpaired : 0;
    }),
  );
  const places = cheapestPairing(costs)
    .slice(0, written.length)
    .filter((column) => column < stored.length);
  const rising = risingRun(places);
  const marks: StrokeMark[] = stored.map(() => "missing");

  places.forEach((place, at) => {
    marks[place] = rising.has(at) ? "right" : "order";
  });

  return { marks, extra: written.length - places.length };
}
